import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { friendlyErrorMessage } from "@/platform/lib/error-messages";
import { ArrowUpRight, Download, Loader2, Search, XCircle } from "lucide-react";

import { AdminShell } from "@/platform/layout/admin-shell";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { Input } from "@/platform/ui/input";
import {
  actOnAdminSubscription,
  listAdminSubscriptions,
  type AdminSubRow,
} from "@/platform/admin/api";
import { cn } from "@/platform/lib/cn";
import { downloadCsv } from "@/platform/lib/csv";

const STATUSES = [
  "all",
  "trialing",
  "active",
  "past_due",
  "unpaid",
  "canceled",
  "incomplete",
  "incomplete_expired",
  "paused",
];

export function AdminSubscriptionsPage() {
  const [search, setSearch] = useState("");
  const [tenant, setTenant] = useState<"all" | "einstein" | "usp">("all");
  const [status, setStatus] = useState<string>("all");
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const queryClient = useQueryClient();
  const [cancel, setCancel] = useState<null | { sub: AdminSubRow; immediate: boolean }>(null);

  const query = useQuery({
    queryKey: ["admin", "subscriptions", { search, tenant, status, page }],
    queryFn: () =>
      listAdminSubscriptions({
        search: search.trim() || undefined,
        tenant,
        status,
        page,
        pageSize,
      }),
    staleTime: 20_000,
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, immediate }: { id: string; immediate: boolean }) =>
      actOnAdminSubscription(id, { type: "cancel", immediate }),
    onSuccess: () => {
      toast.success("Cancelamento solicitado. Stripe vai sincronizar via webhook.");
      void queryClient.invalidateQueries({ queryKey: ["admin", "subscriptions"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "overview"] });
      setCancel(null);
    },
    onError: (err) => toast.error(friendlyErrorMessage(err, "Falha ao cancelar.")),
  });

  const totalPages = query.data ? Math.max(1, Math.ceil(query.data.total / pageSize)) : 1;
  const [exporting, setExporting] = useState(false);

  async function handleExport() {
    setExporting(true);
    try {
      const all = await listAdminSubscriptions({
        search: search.trim() || undefined,
        tenant,
        status,
        page: 1,
        pageSize: 1000,
      });
      if (all.subscriptions.length === 0) {
        toast.info("Sem assinaturas para exportar com os filtros atuais.");
        return;
      }
      downloadCsv(
        `assinaturas_${new Date().toISOString().slice(0, 10)}.csv`,
        all.subscriptions,
        [
          { key: "user_email", header: "Email" },
          { key: (s) => s.user_name ?? "", header: "Nome" },
          { key: "tenant", header: "Tenant" },
          { key: "plan", header: "Plano" },
          { key: "status", header: "Status" },
          {
            key: (s) =>
              s.trial_end ? new Date(s.trial_end).toLocaleDateString("pt-BR") : "",
            header: "Trial até",
          },
          {
            key: (s) =>
              s.current_period_end
                ? new Date(s.current_period_end).toLocaleDateString("pt-BR")
                : "",
            header: "Próxima cobrança",
          },
          {
            key: (s) =>
              s.canceled_at ? new Date(s.canceled_at).toLocaleDateString("pt-BR") : "",
            header: "Cancelada em",
          },
          {
            key: (s) => new Date(s.created_at).toLocaleDateString("pt-BR"),
            header: "Criada em",
          },
          { key: (s) => s.stripe_subscription_id ?? "", header: "Stripe sub_id" },
        ],
      );
      toast.success(`Exportadas ${all.subscriptions.length} assinaturas.`);
    } catch (err) {
      toast.error(friendlyErrorMessage(err, "Falha ao exportar."));
    } finally {
      setExporting(false);
    }
  }

  return (
    <AdminShell
      title="Assinaturas"
      description="Lista de todas as assinaturas Stripe da plataforma. Cancele aqui sem precisar abrir o Stripe Dashboard."
      actions={
        <Button variant="outline" size="sm" onClick={handleExport} loading={exporting}>
          <Download className="size-3.5" />
          Exportar CSV
        </Button>
      }
    >
      <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
        <div>
          <label className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
            Buscar
          </label>
          <div className="mt-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[color:var(--color-muted-foreground)]" aria-hidden />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="email, nome ou stripe sub_id"
              className="pl-9"
            />
          </div>
        </div>
        <FilterSelect
          label="Tenant"
          value={tenant}
          onChange={(v) => {
            setTenant(v as "all" | "einstein" | "usp");
            setPage(1);
          }}
          options={[
            { value: "all", label: "Todos" },
            { value: "einstein", label: "Einstein" },
            { value: "usp", label: "USP" },
          ]}
        />
        <FilterSelect
          label="Status"
          value={status}
          onChange={(v) => {
            setStatus(v);
            setPage(1);
          }}
          options={STATUSES.map((s) => ({ value: s, label: s === "all" ? "Todos" : s }))}
        />
      </div>

      <div className="mt-6 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] overflow-hidden">
        {query.isLoading ? (
          <div className="p-10 flex items-center justify-center">
            <Loader2 className="size-6 animate-spin text-[color:var(--color-muted-foreground)]" />
          </div>
        ) : query.error ? (
          <p className="p-10 text-[color:var(--color-danger)] text-[length:var(--text-body-sm)]">
            Falha ao carregar assinaturas.
          </p>
        ) : !query.data || query.data.subscriptions.length === 0 ? (
          <p className="p-10 text-center text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
            Nenhuma assinatura com esses filtros.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-[length:var(--text-body-sm)]">
                <thead className="bg-[color:var(--color-muted)]/50">
                  <tr>
                    <Th>Usuária</Th>
                    <Th>Plano</Th>
                    <Th>Status</Th>
                    <Th>Trial / Período</Th>
                    <Th className="text-right">Ações</Th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.subscriptions.map((s) => (
                    <SubRow
                      key={s.id}
                      sub={s}
                      onCancel={(immediate) => setCancel({ sub: s, immediate })}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between gap-4 border-t border-[--color-border] px-4 py-3 text-[length:var(--text-body-sm)]">
              <span className="text-[color:var(--color-muted-foreground)]">
                {query.data.total.toLocaleString("pt-BR")} assinaturas · página {page} de {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Próxima
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {cancel && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
          onClick={() => setCancel(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display text-[length:var(--text-body-lg)] font-semibold">
              Cancelar assinatura?
            </h3>
            <p className="mt-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
              {cancel.immediate
                ? "Cancelamento IMEDIATO: a usuária perde acesso agora. Stripe pode emitir refund proporcional."
                : "Cancelamento no fim do período: acesso continua até a próxima cobrança, e não há nova cobrança depois."}
            </p>
            <p className="mt-3 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
              {cancel.sub.user_email} · {cancel.sub.tenant} · {cancel.sub.plan}
            </p>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => setCancel(null)}>
                Voltar
              </Button>
              <Button
                variant="primary"
                onClick={() =>
                  cancelMutation.mutate({ id: cancel.sub.id, immediate: cancel.immediate })
                }
                loading={cancelMutation.isPending}
              >
                Confirmar cancelamento
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}

function SubRow({
  sub,
  onCancel,
}: {
  sub: AdminSubRow;
  onCancel: (immediate: boolean) => void;
}) {
  const canCancel =
    sub.status === "active" || sub.status === "trialing" || sub.status === "past_due";

  return (
    <tr className="border-t border-[--color-border]">
      <td className="px-4 py-3">
        <Link to={`/admin/usuarios/${sub.user_id}`} className="hover:underline">
          <span className="font-medium">
            {sub.user_name ?? sub.user_email}
          </span>
        </Link>
        <p className="text-[color:var(--color-muted-foreground)]">{sub.user_email}</p>
      </td>
      <td className="px-4 py-3 capitalize">
        {sub.tenant} · {sub.plan}
      </td>
      <td className="px-4 py-3">
        <Badge variant="outline" className="capitalize">
          {sub.status}
        </Badge>
      </td>
      <td className="px-4 py-3 text-[color:var(--color-muted-foreground)]">
        {sub.trial_end && new Date(sub.trial_end) > new Date()
          ? `Trial até ${new Date(sub.trial_end).toLocaleDateString("pt-BR")}`
          : sub.current_period_end
            ? `Renovação ${new Date(sub.current_period_end).toLocaleDateString("pt-BR")}`
            : sub.canceled_at
              ? `Cancelada ${new Date(sub.canceled_at).toLocaleDateString("pt-BR")}`
              : "—"}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex gap-1">
          {canCancel && (
            <>
              <Button variant="ghost" size="sm" onClick={() => onCancel(false)}>
                Cancelar no fim
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onCancel(true)}
                className="text-[color:var(--color-danger)]"
              >
                <XCircle className="size-3.5" />
                Imediato
              </Button>
            </>
          )}
          <Button asChild variant="ghost" size="sm">
            <Link to={`/admin/usuarios/${sub.user_id}`}>
              <ArrowUpRight className="size-3.5" />
            </Link>
          </Button>
        </div>
      </td>
    </tr>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        "px-4 py-3 text-left text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)] font-medium",
        className,
      )}
    >
      {children}
    </th>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div>
      <label className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full sm:w-auto rounded-lg border border-[--color-border] bg-[color:var(--color-background)] px-3.5 py-2.5 text-[length:var(--text-body-sm)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
